import { useEffect, useState } from 'react'
import { configClient } from '../services/configClient'

type Props = {
  /** Path lógico relativo al storage root (ej. "tenants/1/config/logo.png"). */
  path: string | null | undefined
  alt: string
  className?: string
  /** Texto mostrado mientras no hay imagen (no nulo) — fallback inline. */
  placeholder?: React.ReactNode
}

/**
 * Renderiza una imagen protegida pidiendo primero una URL firmada al backend.
 *
 * Flujo:
 *   1. configClient.signFile(path) → { url: "/api/files/<token>" }
 *   2. <img src={signedUrl}> — el endpoint /api/files/{token} sirve el binario sin auth header.
 *
 * El token tiene TTL corto (5 min); si el usuario deja la vista abierta más tiempo
 * y la imagen aún no cargó, el browser puede recibir 401. Este componente no refresca
 * la URL automáticamente (raro en este flujo); el usuario puede recargar la página.
 */
export function ProtectedImage({ path, alt, className, placeholder = null }: Props) {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!path) {
      setUrl(null)
      setError(null)
      return
    }
    let cancelled = false
    configClient
      .signFile(path)
      .then((signed) => {
        if (!cancelled) setUrl(signed.url)
      })
      .catch(() => {
        if (!cancelled) setError('No se pudo cargar la imagen.')
      })
    return () => {
      cancelled = true
    }
  }, [path])

  if (!path) return <>{placeholder}</>
  if (error) return <span className="muted">{error}</span>
  if (!url) return <span className="muted">Cargando…</span>

  // Resuelve URL relativa contra el base de la API (el backend usa cookie/proxy en dev).
  const apiBase = import.meta.env.VITE_API_URL ?? '/api'
  const absoluteUrl = url.startsWith('http') ? url : apiBase.replace(/\/api\/?$/, '') + url

  return <img src={absoluteUrl} alt={alt} className={className} />
}
